import React, { useEffect } from "react";
import { Repeat } from "lucide-react";

// Small pieces shared by the dashboard page and its dialogs.

// Closes on Escape and locks page scroll while a dialog is open.
export function useDialog(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
}

export function DialogFrame({ labelledBy, onClose, children, maxWidth = 1040 }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0 sm:items-end">
      <div className="absolute inset-0 bg-[#0f0f1499] backdrop-blur-sm dialog-fade" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        style={{ maxWidth }}
        className="dialog-pop relative w-full max-h-[92vh] sm:max-h-[94vh] flex flex-col overflow-hidden rounded-3xl sm:rounded-b-none bg-[#F6F6F8] shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
      >
        {children}
      </div>
      <style>{`
        .dialog-fade { animation: dialogFade .2s ease; }
        .dialog-pop { animation: dialogPop .28s cubic-bezier(.2,.9,.3,1.2); }
        @keyframes dialogFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dialogPop { from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: none } }
      `}</style>
    </div>
  );
}

export function DialogSection({ title, action, children }) {
  return (
    <section className="rounded-3xl bg-[#fff] border border-[#EEEEF1] p-5 sm:p-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

const AVATAR_COLORS = ["#EA352B", "#FF8A3D", "#1B1B22", "#F5B400", "#6B7280"];

export function Avatar({ name, rank, size = 36 }) {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const bg = AVATAR_COLORS[name.length % AVATAR_COLORS.length];
  return (
    <span className="relative shrink-0">
      <span
        className="rounded-xl flex items-center justify-center font-semibold text-[#fff]"
        style={{ background: bg, width: size, height: size, fontSize: Math.round(size / 3) }}
      >
        {initials}
      </span>
      {rank && rank <= 3 && (
        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#F5B400] text-[9px] font-bold text-[#fff] flex items-center justify-center border-2 border-[#fff]">
          {rank}
        </span>
      )}
    </span>
  );
}

const STATUS_STYLES = {
  Completed: "bg-[#DCFCE7] text-[#16A34A]",
  Active: "bg-[#DCFCE7] text-[#16A34A]",
  "In Chat": "bg-[#DBEAFE] text-[#2563EB]",
  Busy: "bg-[#FFEDD5] text-[#EA580C]",
  Pending: "bg-[#FEF3C7] text-[#B45309]",
  Rejected: "bg-[#FDECEA] text-[#EA352B]",
};

export const STATUS_DOTS = {
  Pending: "#F59E0B",
  "In Chat": "#2563EB",
  Completed: "#16A34A",
  Rejected: "#EA352B",
};

export function VisitsBadge({ visits }) {
  if (visits <= 1) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[#F3F4F6] text-[#6B7280]">
        First visit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[#FFF1E6] text-[#EA580C]">
      <Repeat size={11} /> {visits} visits
    </span>
  );
}

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${STATUS_STYLES[status] || "bg-[#F3F4F6] text-[#6B7280]"}`}>
      {status}
    </span>
  );
}

// Same look as the plate preview in the app's Add Vehicle screen.
export function PlateBadge({ car }) {
  return (
    <div className="flex h-14 rounded-xl border-2 border-[#1A1A2E] overflow-hidden bg-[#fff]">
      <div className="w-12 bg-[#1A1A2E] flex flex-col items-center justify-center shrink-0">
        <span className="text-base leading-none">🇦🇪</span>
        <span className="mt-0.5 text-[8px] font-bold tracking-widest text-[#fff]">UAE</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col items-center justify-center px-2">
        <span className="text-xl font-bold tracking-[0.12em] text-[#1A1A2E] truncate max-w-full">
          {car.plateCode || "- -"} {car.plate || "- - -"}
        </span>
        <span className="text-[9px] text-[#9CA3AF] leading-none">{car.plateSource}</span>
      </div>
      <div className="w-16 bg-[#EA352B] flex items-center justify-center px-1 shrink-0">
        <span className="text-[9px] font-bold text-[#fff] text-center">{car.plateCategory || "Type"}</span>
      </div>
    </div>
  );
}
